import {Component, OnInit, OnDestroy, HostListener} from '@angular/core';
import {Sidenavaction} from '../../../../../../assets/js/application/sidenavaction';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import * as moment from 'moment';
import {BlindFeedSettingsService} from '../../../../../services/blindFeedSettings.service';
import BlindFeedSettingModel from '../../../../../services/blindFeedSettings.model';
import {SpeciesTypeService} from '../../../../../services/speciesType.service';
import SpeciesTypeModel from '../../../../../services/speciesType.model';
import {ToastrService} from 'ngx-toastr';
import { CommonMethodsService } from 'src/app/services/commonMethods.service';
import { LocationStrategy } from '@angular/common';
import { PreviousRouteService } from 'src/app/services/previousRouteService';
import {BatchLoader} from "../../../../../Utils/batch-loader.util";

declare var $: any;

/**
 * BlindFeedSettingsComponent
 *
 * This component is responsible for managing Blind Feed Settings within the Pond Management module.
 * It provides a user interface for administrators to view, create, update, and search for blind feed settings.
 * The component implements infinite scrolling for efficient data display and uses reactive forms for data entry.
 *
 * Key Responsibilities:
 * - Fetch and display a list of all blind feed settings.
 * - Provide search functionality to filter settings by species type name.
 * - Manage the creation of new blind feed settings with validation.
 * - Handle updates to existing blind feed settings.
 * - Implement infinite scrolling using BatchLoader to handle large datasets.
 * - Manage UI state for loading indicators, form visibility, and submission status.
 *
 * Features:
 * - **CRUD Operations**: Create and Update blind feed settings.
 * - **Real-time Search**: Filter list by species type name.
 * - **Infinite Scroll**: Load data in batches to improve performance.
 * - **Form Validation**: Ensures numeric inputs for feed counts and days.
 * - **Date Formatting**: Formats 'updatedAt' timestamps for display.
 *
 * Data Flow:
 * 1. Component initializes (`ngOnInit`).
 * 2. Fetches all blind feed settings from `BlindFeedSettingsService`.
 * 3. Fetches all species types from `SpeciesTypeService` for dropdown selection.
 * 4. Formats dates using `moment`.
 * 5. Initializes `BatchLoader` with the full dataset.
 * 6. User interactions (scroll, search, add/edit) trigger state updates and service calls.
 * 7. `addUpdateBlindFeedSettings` sends data to backend and refreshes the list on success.
 *
 * @component
 * @selector app-blind-feed-settings
 * @example
 * <app-blind-feed-settings></app-blind-feed-settings>
 *
 * @implements {OnInit}
 * @implements {OnDestroy}
 * @author Generated by CompDoc
 * @version 1.0.0
 */
@Component({
  selector: 'app-blind-feed-settings',
  templateUrl: './blind-feed-settings.component.html',
  styleUrls: ['./blind-feed-settings.component.scss']
})
export class BlindFeedSettingsComponent implements OnInit, OnDestroy {
  /**
   * Array holding all fetched blind feed settings from the backend.
   * Acts as the source of truth for the list before filtering.
   * @type {BlindFeedSettingModel[]}
   * @default []
   */
  public blindFeedSettings: BlindFeedSettingModel[] = [];

  /**
   * Array holding the currently displayed blind feed settings.
   * This list is populated by the BatchLoader (subset of blindFeedSettings or filteredBlindFeedSettings).
   * @type {BlindFeedSettingModel[]}
   * @default []
   */
  public blindFeedSettingsList: BlindFeedSettingModel[] = [];

  /**
   * Model for the current blind feed setting being added or edited.
   * Bound to the form for data manipulation.
   * @type {BlindFeedSettingModel}
   */
  public blindFeedSetting: BlindFeedSettingModel = new BlindFeedSettingModel();

  /**
   * Array holding blind feed settings that match the current search criteria.
   * Used as the source for BatchLoader when filtering is active.
   * @type {BlindFeedSettingModel[]}
   * @default []
   */
  public filteredBlindFeedSettings: BlindFeedSettingModel[] = [];

  /**
   * Array of available species types for selection in the form.
   * @type {Array<SpeciesTypeModel>}
   */
  public speciesTypes: Array<SpeciesTypeModel>;

  /**
   * Current search query string entered by the user.
   * @type {string}
   */
  public search: string;

  /**
   * Flag indicating if the form is in 'Create' mode.
   * @type {boolean}
   */
  public submitButton: boolean;

  /**
   * Flag indicating if the form is in 'Update' mode.
   * @type {boolean}
   */
  public updateButton: boolean;

  /**
   * Reactive form group for blind feed setting details.
   * @type {FormGroup}
   */
  public registerForm: FormGroup;

  /**
   * Flag indicating if the form has been submitted.
   * Used to trigger validation error display in the UI.
   * @type {boolean}
   */
  public submitted: boolean;

  /**
   * Flag to prevent double clicks on submit actions.
   * @type {boolean}
   */
  public blindFeedDoubleClick: boolean;

  /**
   * Flag indicating if no data is available for display.
   * @type {boolean}
   * @default false
   */
  public NoDataMessage: boolean = false;

  /**
   * Active page number (not actively used in current logic but declared).
   * @type {number}
   */
  public activePageNo: number;

  /**
   * Flag to trigger clearing of the search input field.
   * @type {boolean}
   * @default false
   */
  public clearSearchInput: boolean = false;

  /**
   * Flag to control the visibility of the main page loader.
   * @type {boolean}
   * @default false
   */
  public enableDisablePageLoader: boolean = false;

  /**
   * Flag to control the visibility of the list loader spinner during infinite scroll.
   * @type {boolean}
   * @default false
   */
  public listLoader = false;              // Show listLoader spinner

  /**
   * Tracks the number of items currently loaded by the batch loader.
   * @type {number}
   * @default 0
   */
  public startIndex = 0;             // Tracks how much has been loaded

  /**
   * Flag indicating if a search filter is currently active.
   * @type {boolean}
   * @default false
   */
  public isFiltering: boolean = false;

  /**
   * Flag indicating if the list is empty (no data or no search results).
   * @type {boolean}
   * @default false
   */
  public noList: boolean = false;

  /**
   * Timeout reference for search input debouncing.
   * @type {any}
   */
  searchTimeout: any;

  /**
   * Count of fields in the form, used for layout adjustments.
   * @type {number}
   * @default 0
   */
  public formFieldCount: number = 0;

  /**
   * Flag to toggle the visibility of the add/edit form.
   * @type {boolean}
   * @default false
   */
  public showForm: boolean = false;

  /**
   * Constructor for BlindFeedSettingsComponent
   *
   * Initializes the component, injects required services, and sets up the reactive form.
   *
   * Form Validation Rules:
   * - species_type_id: Required.
   * - count: Required, Min 3, Max 300, Numeric pattern.
   * - initial_feed: Required, Min 1, Max 300, Numeric pattern.
   * - one_to_seven_days_feed: Required, Min 1, Max 300, Numeric pattern.
   * - seven_to_fourteen_days_feed: Required, Min 1, Max 300, Numeric pattern.
   * - fourteen_to_thirty_days_feed: Required, Min 1, Max 300, Numeric pattern.
   *
   * @constructor
   * @param {ToastrService} toasterService - Service for displaying toast notifications.
   * @service ToastrService
   * @param {SpeciesTypeService} speciesTypeService - Service for Species Type API operations.
   * @service SpeciesTypeService
   * @param {BlindFeedSettingsService} blindFeedSettingsService - Service for Blind Feed Settings API operations.
   * @service BlindFeedSettingsService
   * @param {Sidenavaction} sidenavaction - Service for side navigation actions.
   * @service Sidenavaction
   * @param {FormBuilder} formBuilder - Service for building reactive forms.
   * @service FormBuilder
   * @param {CommonMethodsService} commonMethodsService - Service for common utility methods and error handling.
   * @service CommonMethodsService
   * @param {LocationStrategy} locationStrategy - Service for interacting with the browser's URL.
   * @service LocationStrategy
   * @param {PreviousRouteService} previousRouteService - Service for tracking navigation history.
   * @service PreviousRouteService
   */
  constructor(private toasterService: ToastrService, public speciesTypeService: SpeciesTypeService, public blindFeedSettingsService: BlindFeedSettingsService, public sidenavaction: Sidenavaction, public formBuilder: FormBuilder, public commonMethodsService: CommonMethodsService,
    private locationStrategy: LocationStrategy, private previousRouteService: PreviousRouteService
  ) {
    this.blindFeedSetting = new BlindFeedSettingModel();
    /*form validation conditions*/
    this.registerForm = this.formBuilder.group({
      species_type_id: ['', [Validators.required]],
      count: ['', [Validators.required,
        Validators.minLength(3),
        Validators.maxLength(300),
        Validators.pattern('^[0-9]*$')]],
      initial_feed: ['', [Validators.required,
        Validators.minLength(1),
        Validators.maxLength(300),
        Validators.pattern('^[0-9]*$')]],
      one_to_seven_days_feed: ['', [Validators.required,
        Validators.minLength(1),
        Validators.maxLength(300),
        Validators.pattern('^[0-9]*$')]],
      seven_to_fourteen_days_feed: ['', [Validators.required,
        Validators.minLength(1),
        Validators.maxLength(300),
        Validators.pattern('^[0-9]*$')]],
      fourteen_to_thirty_days_feed: ['', [Validators.required,
        Validators.minLength(1),
        Validators.maxLength(300),
        Validators.pattern('^[0-9]*$')]],
    });
    // Calculate field count after form is built // new
    this.formFieldCount = Object.keys(this.registerForm.controls).length;
  }

  /**
   * Utility for handling batch loading of data for infinite scroll.
   * @type {BatchLoader<any>}
   * @private
   */
  private batchLoader: BatchLoader<any>;

  /**
   * Angular lifecycle hook - Component initialization
   *
   * Performs the following initialization steps:
   * 1. Sets up side navigation.
   * 2. Resets component state and form.
   * 3. Tracks the current route.
   * 4. Fetches all blind feed settings from the backend.
   * 5. Fetches all species types for dropdowns.
   * 6. Formats the 'updatedAt' date for each setting.
   * 7. Initializes the batch loader and loads the first batch of data.
   *
   * @async
   * @method ngOnInit
   * @returns {Promise<void>}
   * @lifecycle Angular component initialization hook
   */
  public async ngOnInit() {
    this.sidenavaction.x_navigation();
    window.scrollTo(0, 0);
    this.enableDisablePageLoader = true;
    this.clear();
    this.blindFeedSettings = [];
    this.blindFeedSettingsList = [];
    this.filteredBlindFeedSettings = [];
    this.isFiltering = false;
    this.search = '';
    this.previousRouteService.setPreviousRoute(this.locationStrategy.path());
    try {
      await this.getBlindFeedSettings();
      this.speciesTypes = await this.speciesTypeService.getAllSpeciesType();
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * Fetches all blind feed settings from the backend.
   * Formats dates and initializes the batch loader.
   *
   * @async
   * @method getBlindFeedSettings
   * @returns {Promise<void>}
   * @throws {Error} If the service call fails.
   */
  public async getBlindFeedSettings() {
    try {
      this.blindFeedSettings = await this.blindFeedSettingsService.getAllBFWithSpeciesType();
      if (this.blindFeedSettings.length > 0) {
        this.blindFeedSettings.forEach(eachSpeciesType => {
          eachSpeciesType.updatedAt = JSON.parse(JSON.stringify(moment(eachSpeciesType.updatedAt).format('DD-MMM-YYYY hh:mm:ss A')));
        });
      }
      this.initializeBatchLoader(); // Reset instance
      this.batchLoader?.reset();     // Clear state
      await this.batchLoader?.loadNextBatch(true); // Load first batch
      this.enableDisablePageLoader = false;
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
      this.enableDisablePageLoader = false;
    }
  }

  /**
   * Host listener for window scroll events.
   * Triggers the check for loading the next batch of data when scrolling near the bottom.
   *
   * @method onScroll
   * @returns {void}
   */
  @HostListener('window:scroll', [])
  onScroll(): void {
    this.checkScrollAndLoad();
  }

  /**
   * Timeout reference for scroll event debouncing.
   * @type {any}
   * @private
   */
  private scrollTimeout: any;

  /**
   * Checks scroll position and loads next batch if near bottom.
   * Debounced to prevent excessive calls.
   *
   * @method checkScrollAndLoad
   * @private
   * @returns {void}
   */
  private checkScrollAndLoad(): void {
    clearTimeout(this.scrollTimeout);
    this.scrollTimeout = setTimeout(() => {
      if ((window.innerHeight + window.scrollY) >= document.body.offsetHeight - 100 && !this.batchLoader?.isLoading()) {
        this.batchLoader?.loadNextBatch();
      }
    }, 50);
  }

  /**
   * Initializes the BatchLoader instance.
   * Configures the source list (filtered or full) and the callback to update the displayed list.
   *
   * @method initializeBatchLoader
   * @private
   * @returns {void}
   */
  private initializeBatchLoader(): void {
    this.batchLoader = new BatchLoader<any>(
      () => this.isFiltering ? this.filteredBlindFeedSettings : this.blindFeedSettings,
      (updatedList) => {
        this.blindFeedSettingsList = updatedList;
        this.noList = updatedList.length === 0;
      },
      undefined, // <-- omit or pass undefined
      (loading) => this.listLoader = loading
    );
  }

  /**
   * Angular lifecycle hook - Component destruction
   *
   * Cleans up resources:
   * - Cancels ongoing HTTP requests for BlindFeedSettingsService and SpeciesTypeService.
   * - Destroys and resets the batch loader.
   *
   * @method ngOnDestroy
   * @returns {void}
   * @lifecycle Angular component destruction hook
   */
  public ngOnDestroy(): void {
    this.blindFeedSettingsService.cancelOnGoingHttpRequests();
    this.speciesTypeService.cancelOnGoingHttpRequests();
    this.batchLoader?.markAsDestroyed();
    this.batchLoader?.reset();
  }

  /**
   * Getter for easy access to form controls in the template.
   *
   * @method blindFeedSettingsFormValidation
   * @returns {AbstractControl} Form controls
   */
  public get blindFeedSettingsFormValidation() {
    return this.registerForm.controls;
  }

  /**
   * Filters the blind feed settings list based on the search query.
   *
   * - Filters by species type name (case-insensitive).
   * - Updates `filteredBlindFeedSettings`.
   * - Resets the batch loader to display filtered results.
   *
   * @async
   * @method applySearch
   * @returns {Promise<void>}
   * @side-effect Updates `filteredBlindFeedSettings`, `blindFeedSettingsList`, `noList`, `isFiltering`.
   */
  public async applySearch(): Promise<void> {
    const query = this.search?.trim().toLowerCase() || '';
    this.isFiltering = query.length > 0;
    const sourceList = this.isFiltering
      ? this.blindFeedSettings.filter(route =>
        route.speciesType.name?.toLowerCase().includes(query)
      )
      : this.blindFeedSettings;
    this.filteredBlindFeedSettings = this.isFiltering ? sourceList : [];
    this.batchLoader?.reset(true);
    await this.batchLoader?.loadNextBatch(true);
    if (sourceList.length === 0) {
      //  Force noList = true if nothing to show
      this.noList = true;
      this.blindFeedSettingsList = [];
      return;
    }
    // optional: handle scroll if user already at bottom
    setTimeout(() => this.checkScrollAndLoad(), 100);
  }

  /**
   * Debounces search input changes.
   * Calls `applySearch` after a delay to prevent excessive filtering.
   *
   * @method onSearchChanged
   * @returns {void}
   */
  public onSearchChanged(): void {
    clearTimeout(this.searchTimeout);
    this.searchTimeout = setTimeout(() => {
      this.applySearch(); // debounced
    }, 300);
  }

  /**
   * Resets the form and component state to default values.
   *
   * @method clear
   * @returns {void}
   */
  public clear() {
    this.registerForm.reset();
    this.showForm = false;
    this.blindFeedSetting = new BlindFeedSettingModel();
    this.blindFeedDoubleClick = false;
    this.submitButton = false;
    this.updateButton = false;
    this.submitted = false;
  }

  /**
   * Handles the submission of the Blind Feed Settings form (Add or Update).
   *
   * - Validates the form.
   * - Calls `create` or `updateOneByID` service methods based on mode.
   * - Displays success notification.
   * - Refreshes the list via `ngOnInit`.
   *
   * @async
   * @method addUpdateBlindFeedSettings
   * @returns {Promise<void>}
   * @throws {Error} If the service call fails.
   */
  public async addUpdateBlindFeedSettings() {
    this.submitted = true;
    // stop here if form is invalid // need to check with anusha
    if (this.registerForm.invalid) {
      return;
    }
    try {
      this.blindFeedDoubleClick = true;
      // GET FORM VALUES
      const formData = this.registerForm.value; // contains updated values
      if (this.submitButton) {
        await this.blindFeedSettingsService.create(formData);
        this.toasterService.success('Blind feed setting created successfully', '', {
          timeOut: 3000
        });
      } else {
        const updateBlindFeed = {...formData}
        delete updateBlindFeed.id;
        await this.blindFeedSettingsService.updateOneByID(this.blindFeedSetting.id, updateBlindFeed);
        this.toasterService.success('Blind feed setting updated successfully', '', {
          timeOut: 3000
        });
      }
      await this.ngOnInit();
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
      this.blindFeedDoubleClick = false;
    }
  }

  /**
   * Retrieves a Blind Feed Setting by ID and populates the form for editing.
   * Sets the component to 'Update' mode.
   *
   * @method getBlindFeedSetingsById
   * @param {string} id - The ID of the blind feed setting to edit.
   * @returns {void}
   */
  public getBlindFeedSetingsById(id: string) {
    this.showForm = true;
    this.submitButton = false;
    this.blindFeedDoubleClick = false;
    this.submitButton = false;
    this.updateButton = true;
    const foundBlindFeedSetting = this.blindFeedSettingsList.find(uom => uom?.id === id);
    this.blindFeedSetting = foundBlindFeedSetting ? { ...foundBlindFeedSetting } : new BlindFeedSettingModel(); // clone object
    if (this.blindFeedSetting) {
      this.registerForm.patchValue({
        species_type_id: this.blindFeedSetting.species_type_id,
        count: this.blindFeedSetting.count,
        initial_feed: this.blindFeedSetting.initial_feed,
        one_to_seven_days_feed: this.blindFeedSetting.one_to_seven_days_feed,
        seven_to_fourteen_days_feed: this.blindFeedSetting.seven_to_fourteen_days_feed,
        fourteen_to_thirty_days_feed: this.blindFeedSetting.fourteen_to_thirty_days_feed
      });
    }
  }

  /**
   * Opens the form in 'Create' mode.
   *
   * @method openForm
   * @returns {void}
   */
  public openForm() {
    this.showForm = true;
    this.submitButton = true;
    this.updateButton = false;
    // Count form fields to toggle layout
    this.formFieldCount = Object.keys(this.registerForm.controls).length;
  }
}
